import styled, { ThemeProvider } from 'styled-components';
import theme from '../styles/theme';
import { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { PoliceReportsService } from '../services/police-reports-service';
import { useLocation } from 'react-router-dom';
import LoginForm from '../components/login/LoginForm';
import RegisterForm from '../components/login/RegisterForm';
import UpdatePasswordForm from '../components/login/UpdatePasswordForm';

const Container = styled.div`  
    padding-top: 3rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.regular};
    &:before, &:after {
      content: "";
      display: block;
      clear: both;
    }
`;

const FormBox = styled.div`
    position: relative;
    z-index: 1;
    background: #FFFFFF;
    max-width: 360px;
    margin: 0 auto 100px;
    padding: 45px;
    text-align: center;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2), 0 5px 5px 0 rgba(0, 0, 0, 0.24);
    border-radius: .25rem;
`;

function Login() {
    const [formState, setFormState] = useState<'login' | 'register' | 'changePassword'>('login');
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState("");
    const [loginError, setLoginError] = useState("");
    const location = useLocation();

    useEffect(() => {
        if (location.state && location.state.formState) {
            setFormState(location.state.formState);
        }
    }, [location.state]);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    }

    const clickLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailError(""); // Clear previous errors
        setLoginError(""); // Clear previous errors

        if (!validateEmail(email)) {
            setEmailError("Invalid email format");
            return;
        }

        if (!password) {
            setLoginError("Password cannot be empty");
            return;
        }

        try {
            const token = await PoliceReportsService.authenticateUser(email, password);
            if (token) {
                login(token); // Call the login function from useAuth to update the auth state
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginError("Authentication failed. Please check your email and password.");
        }
    };


    return (
        <ThemeProvider theme={theme}>
            <Container>
                <FormBox>
                    {formState === 'login' && (
                        <LoginForm
                            email={email}
                            setEmail={setEmail}
                            emailError={emailError}
                            setEmailError={setEmailError}
                            loginError={loginError}
                            password={password}
                            setPassword={setPassword}
                            clickLogin={clickLogin}
                            setFormState={setFormState}
                        />
                    )}
                    {formState === 'register' && (
                        <RegisterForm
                            email={email}
                            setEmail={setEmail}
                            emailError={emailError}
                            setEmailError={setEmailError}
                            setFormState={setFormState}
                        />
                    )}
                    {formState === 'changePassword' && (
                        <UpdatePasswordForm
                            email={email}
                            setEmail={setEmail}
                            emailError={emailError}
                            setEmailError={setEmailError}
                            setFormState={setFormState}
                        />
                    )}
                </FormBox>
            </Container>
        </ThemeProvider>
    );
}

export default Login;